import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";

// =============================================================================
// The product catalogue.
//
// Every mutation invalidates the product list AND the brand list: adding the
// first product changes what the generator can do for that brand, and the
// dashboard reads brand state to decide what to tell the owner next.
// =============================================================================

export type ProductImage = {
  id: string;
  url: string;
  /** The untouched upload, when the background was tidied. Null if not. */
  sourceUrl: string | null;
  isPrimary: boolean;
  sortOrder: number;
};

export type Product = {
  id: string;
  brandId: string;
  name: string;
  description: string | null;
  /** Free text — "GHS 45 a kilo". Reaches captions only, never an image. */
  price: string | null;
  sortOrder: number;
  isActive: boolean;
  /** When a generated post last showed this product. */
  lastFeaturedAt: string | null;
  createdAt: string;
  images: ProductImage[];
};

export function useProducts(brandId: string | null) {
  const workspaceId = useAuthStore((s) => s.workspaceId);
  return useQuery({
    queryKey: ["products", workspaceId, brandId],
    queryFn: () => unwrap<Product[]>(api.get("/products", { params: { brandId } })),
    enabled: !!workspaceId && !!brandId,
  });
}

/** Invalidates everything a new product changes. */
function useRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["products"] });
    void qc.invalidateQueries({ queryKey: ["brands"] });
  };
}

/**
 * Adds a product with its first photograph, as multipart.
 *
 * No Content-Type header is set on purpose: the browser must add the multipart
 * boundary itself, and setting the header by hand omits it, which makes the
 * server see a body it cannot parse.
 */
export function useCreateProduct() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: {
      brandId: string;
      name: string;
      description?: string;
      price?: string;
      /** Blank the background on the way in. Defaults on, server side too. */
      tidy: boolean;
      file: File;
    }) => {
      const form = new FormData();
      form.append("photo", args.file);
      form.append("brandId", args.brandId);
      form.append("name", args.name);
      form.append("tidy", String(args.tidy));
      if (args.description) form.append("description", args.description);
      if (args.price) form.append("price", args.price);
      return unwrap<Product>(api.post("/products", form));
    },
    onSuccess: refresh,
  });
}

export function useUpdateProduct() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: {
      id: string;
      name?: string;
      description?: string | null;
      price?: string | null;
      isActive?: boolean;
    }) => {
      const { id, ...body } = args;
      return unwrap<Product>(api.patch(`/products/${id}`, body));
    },
    onSuccess: refresh,
  });
}

export function useDeleteProduct() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => unwrap<{ id: string }>(api.delete(`/products/${id}`)),
    onSuccess: refresh,
  });
}

export function useAddProductImage() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: {
      productId: string;
      file: File;
      primary?: boolean;
      tidy?: boolean;
    }) => {
      const form = new FormData();
      form.append("photo", args.file);
      if (args.primary) form.append("primary", "true");
      form.append("tidy", String(args.tidy ?? true));
      return unwrap<Product>(api.post(`/products/${args.productId}/images`, form));
    },
    onSuccess: refresh,
  });
}

/** Back to the photograph the owner took, discarding the tidied one. */
export function useRevertProductImage() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: { productId: string; imageId: string }) =>
      unwrap<Product>(
        api.post(`/products/${args.productId}/images/${args.imageId}/revert`, {}),
      ),
    onSuccess: refresh,
  });
}

export function useSetPrimaryImage() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: { productId: string; imageId: string }) =>
      unwrap<Product>(
        api.post(`/products/${args.productId}/images/${args.imageId}/primary`, {}),
      ),
    onSuccess: refresh,
  });
}

export function useDeleteProductImage() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: { productId: string; imageId: string }) =>
      unwrap<Product>(api.delete(`/products/${args.productId}/images/${args.imageId}`)),
    onSuccess: refresh,
  });
}
